import { DOCUMENT, NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ToolbarLanguage } from 'src/app/models/toolbar-language.model';
import { KeycloakService } from 'src/app/services/keycloak/keycloak.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { MatToolbar } from '@angular/material/toolbar';
import {
  DefaultClassDirective,
  DefaultShowHideDirective,
} from '@ngbracket/ngx-layout/extended';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import {
  DefaultLayoutDirective,
  DefaultLayoutAlignDirective,
  DefaultFlexDirective,
  FlexFillDirective,
} from '@ngbracket/ngx-layout/flex';
import { MatIcon } from '@angular/material/icon';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatToolbar,
    NgClass,
    DefaultClassDirective,
    MatButton,
    DefaultShowHideDirective,
    MatMenuTrigger,
    DefaultLayoutDirective,
    DefaultLayoutAlignDirective,
    MatMenu,
    MatMenuItem,
    DefaultFlexDirective,
    FlexFillDirective,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatSelect,
    FormsModule,
    MatOption,
    MatInput,
    MatTooltip,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
  ],
})
export class ToolbarComponent implements OnInit, OnChanges {
  @Input() collapsed = true;
  @Input() screenWidth = 0;
  @Input() isLoggedIn = true;
  @Input({ required: true }) userLanguage = '';
  @Input() groupeEtablissement: string[] = [];
  @Input() selectedEtablissement = '';
  @Output() toggleSidebarForMe = new EventEmitter<void>();

  isSmallScreen = false;
  isNotConnected = true;
  languages: ToolbarLanguage[] = [
    {
      id: 'fr',
      title: 'Français',
    },
    {
      id: 'de',
      title: 'Deutsch',
    },
  ];
  selectedLanguage: ToolbarLanguage;

  hasMultiEtab = false;
  etabSearch = '';
  filteredOptions: string[];

  constructor(
    private _translateService: TranslateService,
    private _router: Router,
    private _keycloakService: KeycloakService,
    @Inject(DOCUMENT) private document: Document,
    private _localStorageService: LocalStorageService,
    private _settingsService: SettingsService
  ) {
    //Trigger when user change page
    _router.events.subscribe(() => {
      this.verifySize();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userLanguage']) {
      this.initializeTranslation();
    }

    if (changes['groupeEtablissement']) {
      this.initializeGE();
    }
  }

  ngOnInit(): void {
    this.isNotConnected = !this._keycloakService.isAuthenticated();
    this.initializeTranslation();
    this.initializeGE();
    this.onInputChange();
  }

  //Trigger when page size changed
  @HostListener('window:resize')
  onResize() {
    this.verifySize();
  }

  //TODO: Add this as an input property
  private verifySize() {
    if (!this.isSmallScreen && window.innerWidth <= 1280) {
      this.isSmallScreen = true;
    } else if (this.isSmallScreen && window.innerWidth > 1280) {
      this.isSmallScreen = false;
    }
  }

  /**
   * @description
   * Emits an event to indicate sidebar toggle.
   */
  toggleSidenav() {
    this.toggleSidebarForMe.emit(null);
  }

  /**
   * @description
   * Set language application wide, update selected language and
   * load corresponding translations.
   *
   * @param language Two char language code 'fr' or 'de'.
   * @returns Could set language true or false.
   */
  private setLanguage(language: string): boolean {
    const selectedLanguage = this.languages.find(lang => lang.id === language);
    if (selectedLanguage) {
      this.selectedLanguage = selectedLanguage;
      // Use the selected language for translations
      this._translateService.use(this.selectedLanguage.id);
      // Update the lang attribute
      this.document.documentElement.lang = this.selectedLanguage.id;
      return true;
    }
    return false;
  }

  /**
   * @description
   * User change language via the toolbar.
   *
   * @param lang The selected language
   */
  onLanguageToolbarChange(lang: ToolbarLanguage) {
    // Prevents storing an unsupported language
    if (this.setLanguage(lang.id)) {
      this._localStorageService.setItem('userTemporaryLanguage', lang.id);
    }
  }

  //Get navigation class if menu is visible, opened or closed
  getNavClass(): string {
    let styleClass = '';
    if (this.isLoggedIn) {
      styleClass = 'nav-sidenav';
    }
    if (this.collapsed) {
      if (this.screenWidth > 768) {
        styleClass += ' nav-trimmed';
      } else if (this.screenWidth <= 768 && this.screenWidth > 0) {
        styleClass += ' nav-md-screen';
      }
    }
    return styleClass;
  }

  /**
   * Loads default language by priority:
   * 1. Selected language (local storage);
   * 2. User language (midpoint);
   * 3. Browser language.
   */
  private initializeTranslation() {
    let language = '';

    //Automatically use default navigator language
    if (navigator.language.includes('de')) {
      language = 'de';
    } else {
      language = 'fr';
    }

    const selectedLanguageInAppMemory =
      this._localStorageService.getItem<string>('userTemporaryLanguage');

    //If user is authenticated and has not chosen a language
    if (!this.isNotConnected && !selectedLanguageInAppMemory) {
      //Use default user language
      if (this.userLanguage == 'fr') {
        language = 'fr';
      } else if (this.userLanguage == 'de') {
        language = 'de';
      }
      //If language selected
    } else if (selectedLanguageInAppMemory) {
      language = selectedLanguageInAppMemory;
    }

    this.setLanguage(language);
  }

  private initializeGE() {
    this.hasMultiEtab =
      this.groupeEtablissement && this.groupeEtablissement.length > 1;
  }

  onEtablissementChange() {
    this._settingsService.updateCurrentGE(this.selectedEtablissement);
  }

  onSelectClose() {
    this.etabSearch = '';
    this.onInputChange();
  }

  onInputChange() {
    this.filteredOptions = this.groupeEtablissement
      ? this.groupeEtablissement.filter(etab =>
          etab.toLocaleLowerCase().includes(this.etabSearch.toLocaleLowerCase())
        )
      : [];
  }
}
